import { type RefObject, useEffect, useRef, useState } from 'react';

/**
 * Which of `sectionIds` is currently being read inside `scrollerRef` — the
 * Cadrage rail's active marker (issue #3).
 *
 * `rootMargin` shrinks the observation box to the **top 30%** of the scroller.
 * Without that bias, a tall section and a short one are both "intersecting"
 * for most of the scroll and the marker picks whichever the observer reports
 * first, which reads as it flickering at random. Biased this way, the active
 * section is the one under the reader's eye, and there is exactly one.
 *
 * The very last section can be shorter than that band and never reach it, so
 * the hook falls back to the last id once the scroller is at the bottom —
 * otherwise "Prompt" would be unreachable in the rail no matter how far you
 * scroll.
 *
 * Returns the first id when the browser has no `IntersectionObserver` (jsdom
 * under Vitest): a rail whose first entry is marked is a truthful default,
 * whereas marking none of them would render a state the real UI never shows.
 */
export function useSectionSpy(
  sectionIds: readonly string[],
  scrollerRef: RefObject<HTMLElement | null>,
): string | null {
  const key = sectionIds.join(',');
  const [activeId, setActiveId] = useState<string | null>(sectionIds[0] ?? null);
  // Read inside the observer callback, which must see today's id list without
  // being torn down and rebuilt whenever the array identity changes.
  const idsRef = useRef(sectionIds);
  idsRef.current = sectionIds;

  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the serialized id list — `sectionIds` is rebuilt on every render by its caller, so depending on the array itself would re-create the observer each time.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || typeof IntersectionObserver === 'undefined') return;

    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.id;
          if (entry.isIntersecting) visible.add(id);
          else visible.delete(id);
        }
        const atBottom =
          scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= 2 &&
          scroller.scrollHeight > scroller.clientHeight;
        const ids = idsRef.current;
        const next = atBottom
          ? (ids[ids.length - 1] ?? null)
          : (ids.find((id) => visible.has(id)) ?? null);
        // `null` means "nothing is in the band right now", which happens
        // mid-flight between two sections. Keeping the previous value there is
        // what stops the marker from blinking off on every scroll tick.
        if (next !== null) setActiveId(next);
      },
      { root: scroller, rootMargin: '0px 0px -70% 0px', threshold: 0 },
    );

    for (const id of sectionIds) {
      const element = scroller.querySelector(`#${CSS.escape(id)}`);
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [key, scrollerRef]);

  return activeId;
}
